import type { TaskId } from '@/core/content/ids';
import type { Reward } from '@/core/content/schemas/progression';
import type { SuggestionDef } from '@/core/content/schemas/progressionDefs';
import type { TFunction } from '@/core/i18n/translator';
import { hasSwapAlternative, suggestionHintParams, taskProgress } from '@/core/domain/suggestions';
import { useSaveDerived, useServices } from '@/ui/services/ServicesContext';

export interface TaskCard {
  taskId: TaskId;
  instanceKey?: string;
  kind: 'tutorial' | 'optional' | 'repeatable';
  titleKey: string;
  hintKey?: string;
  /** ICU arguments for `hintKey` (T09's `{price}`). */
  hintParams?: { price: number };
  current: number;
  target: number;
  status: 'active' | 'completed' | 'claimed' | 'available' | 'locked';
  rewards: readonly Reward[];
  /** 1-based position in the tutorial (FTUE only). */
  step?: number;
  /** "Đi tới" target (optional / repeatable cards). */
  goto?: SuggestionDef['goto'];
  /** What "Chỉ chỗ" points at (tutorial card). */
  highlight?: SuggestionDef['highlight'];
  /** "Đổi gợi ý" would bring a different card (else S08 says so and hides the button). */
  canSwap?: boolean;
}

/** What S08 shows right now: the current tutorial step during FTUE, otherwise ≤ 3 suggestions (§14.9). */
export function useTaskCards(): { cards: TaskCard[]; tutorialTotal: number; inTutorial: boolean } {
  const { content } = useServices();
  return useSaveDerived((save) => {
    const S = save.suggestions;
    const tutorial = content.suggestions
      .filter((d) => d.kind === 'tutorial')
      .sort((a, b) => a.order - b.order);
    const current = tutorial.find((d) => S.tasks[d.id]?.status !== 'claimed');
    if (current) {
      const rt = S.tasks[current.id];
      const p = taskProgress(content, save, current, rt);
      const hintParams = suggestionHintParams(content, current);
      return {
        inTutorial: true,
        tutorialTotal: tutorial.length,
        cards: [
          {
            taskId: current.id,
            kind: 'tutorial',
            titleKey: current.titleKey,
            ...(current.hintKey ? { hintKey: current.hintKey } : {}),
            ...(hintParams ? { hintParams } : {}),
            ...p,
            status: rt?.status ?? 'active',
            rewards: current.rewards,
            step: tutorial.indexOf(current) + 1,
            ...(current.highlight ? { highlight: current.highlight } : {}),
          },
        ],
      };
    }
    const cards: TaskCard[] = [];
    for (const id of S.optionalActive) {
      const def = content.suggestions.find((d) => d.id === id);
      if (!def) continue;
      const rt = S.tasks[id];
      cards.push({
        taskId: def.id,
        kind: 'optional',
        titleKey: def.titleKey,
        ...taskProgress(content, save, def, rt),
        status: rt?.status ?? 'active',
        rewards: def.rewards,
        ...(def.goto ? { goto: def.goto } : {}),
        canSwap: hasSwapAlternative(content, save, def.id),
      });
    }
    for (const entry of S.repeatable.active) {
      const def = content.suggestions.find((d) => d.id === entry.taskId);
      if (!def) continue;
      cards.push({
        taskId: def.id,
        instanceKey: entry.instanceKey,
        kind: 'repeatable',
        titleKey: def.titleKey,
        ...taskProgress(content, save, def, entry.runtime),
        status: entry.runtime.status,
        rewards: def.rewards,
        ...(def.goto ? { goto: def.goto } : {}),
        canSwap: hasSwapAlternative(content, save, def.id),
      });
    }
    return { inTutorial: false, tutorialTotal: tutorial.length, cards };
  });
}

/** "+1 sao", "+15 xu"… for the reward chip (words, not emoji: not every phone font has 🪙). */
export function rewardLabel(rewards: readonly Reward[], t: TFunction): string {
  return rewards
    .map((r) =>
      r.type === 'stars'
        ? t('ui.reward.stars', { count: r.amount })
        : r.type === 'coins'
          ? t('ui.reward.coins', { count: r.amount })
          : '',
    )
    .filter(Boolean)
    .join(', ');
}
